import React, { useState } from 'react';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Plus, Search, Users as UsersIcon, Loader2 } from 'lucide-react';
import TeamCard from './components/teams/TeamCard';
import TeamModal from './components/teams/TeamModal';
import ConfirmDialog from './components/shared/ConfirmDialog';
import { mockTeams, mockMembers } from './components/data/mockData';

export default function Teams() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isLoading] = useState(false);
  
  const [teams, setTeams] = useState(mockTeams);
  const [members] = useState(mockMembers);

  const handleEdit = (team) => {
    setSelectedTeam(team);
    setIsModalOpen(true);
  };

  const handleDelete = (team) => {
    setDeleteConfirm(team);
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      setTeams(teams.filter(t => t.id !== deleteConfirm.id));
    }
  };

  const handleSubmit = (data) => {
    if (selectedTeam) {
      setTeams(teams.map(t => t.id === selectedTeam.id ? { ...data, id: selectedTeam.id } : t));
      setIsModalOpen(false);
      setSelectedTeam(null);
    } else {
      const newTeam = {
        ...data,
        id: `t${Date.now()}`,
        created_date: new Date().toISOString()
      };
      setTeams([...teams, newTeam]);
      setIsModalOpen(false);
    }
  };

  const filteredTeams = teams.filter(team => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return team.name?.toLowerCase().includes(query) || 
           team.description?.toLowerCase().includes(query);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-pink-50/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-200 ring-4 ring-purple-100">
              <UsersIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Equipes
              </h1>
              <p className="text-sm text-slate-600">{teams.length} equipes cadastradas</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar equipes..."
                className="pl-9 bg-white/80 border-slate-200 shadow-sm"
              />
            </div>
            <Button 
              onClick={() => {
                setSelectedTeam(null);
                setIsModalOpen(true);
              }}
              className="bg-gradient-to-r from-blue-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-lg shadow-blue-200 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Equipe
            </Button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
          </div>
        ) : filteredTeams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <UsersIcon className="w-16 h-16 mb-4" />
            <p className="text-lg font-medium">
              {searchQuery ? 'Nenhuma equipe encontrada' : 'Nenhuma equipe cadastrada'}
            </p>
            {!searchQuery && (
              <Button 
                onClick={() => setIsModalOpen(true)}
                variant="outline"
                className="mt-4"
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar primeira equipe
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTeams.map((team) => (
              <TeamCard
                key={team.id}
                team={team}
                members={members}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Team Modal */}
      <TeamModal
        team={selectedTeam}
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTeam(null);
        }}
        onSubmit={handleSubmit}
        members={members}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title="Excluir Equipe"
        description="Tem certeza que deseja excluir esta equipe? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        variant="danger"
      />
    </div>
  );
}